/**
 * Icon Font System - Replacement for SVG-based Lucide Icons
 * 
 * This system uses Font Awesome icons instead of SVG elements for better performance
 * and consistency across the application.
 */

// Icon mapping from current Lucide names to Font Awesome classes
const iconFontMap = {
  'train': 'fas fa-train',
  'close': 'fas fa-times',
  'dark_mode': 'fas fa-moon',
  'light_mode': 'fas fa-sun',
  'cloud': 'fas fa-cloud',
  'cloud_off': 'fas fa-cloud-slash',
  'my_location': 'fa-solid fa-location-crosshairs',
  'accessible': 'fas fa-wheelchair',
  'short_text': 'fas fa-align-left',
  'text_fields': 'fas fa-text-width',
  'warning': 'fas fa-exclamation-triangle',
  'x-circle': 'fas fa-times-circle',
  'chevron-down': 'fas fa-chevron-down',
  'info': 'fas fa-info-circle',
  'transfer_within_a_station': 'fas fa-exchange-alt',
  'schedule': 'fas fa-clock',
  'clock': 'fas fa-clock',
  'refresh': 'fas fa-sync-alt',
  'error': 'fas fa-exclamation-circle',
  'block': 'fas fa-ban',
  'construction': 'fa-solid fa-person-digging',
  'open_in_new': 'fas fa-external-link-alt',
  'not_accessible': 'fas fa-wheelchair-alt',
  'elevator': 'fa-solid fa-elevator',
  'escalator': 'fa-solid fa-stairs',
  'expand_more': 'fas fa-chevron-down',
  // Weather icons
  'sun': 'fas fa-sun',
  'moon': 'fas fa-moon',
  'cloud_sun': 'fas fa-cloud-sun',
  'cloud_moon': 'fas fa-cloud-moon',
  'cloud_rain': 'fas fa-cloud-rain',
  'snowflake': 'fas fa-snowflake',
  'cloud_bolt': 'fas fa-cloud-bolt',
  'smog': 'fas fa-smog',
  // Washroom icons
  'wc': 'fas fa-restroom'
};

/**
 * Create an icon font element using Font Awesome classes
 * @param {string} iconName - The icon name from our mapping
 * @param {string} className - Additional CSS classes (Tailwind, etc.)
 * @returns {string} HTML string with Font Awesome icon
 */
export function iconFont(iconName, className = '') {
  const fontAwesomeClass = iconFontMap[iconName];
  
  if (!fontAwesomeClass) {
    console.warn(`Icon "${iconName}" not found in Font Awesome mapping`);
    return `<i class="fas fa-question-circle ${className}" title="Missing icon: ${iconName}"></i>`;
  }
  
  return `<i class="${fontAwesomeClass} ${className}"></i>`;
}

/**
 * Create an icon font element with specific size
 * @param {string} iconName - The icon name from our mapping
 * @param {string} size - Size class (text-sm, text-lg, text-xl, etc.)
 * @param {string} className - Additional CSS classes
 * @returns {string} HTML string with Font Awesome icon
 */
export function iconFontSized(iconName, size = 'text-base', className = '') {
  return iconFont(iconName, `${size} ${className}`);
}

/**
 * Initialize Font Awesome by adding the CDN link to the page
 * This should be called once when the page loads
 */
export function initializeFontAwesome() {
  // Check if Font Awesome is already loaded
  if (document.querySelector('link[href*="font-awesome"]')) {
    return;
  }
  
  // Add Font Awesome CSS
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
  link.integrity = 'sha512-iecdLmaskl7CVkqkXNQ/ZH/XLlvWZOJyj7Yy7tcenmpD1ypASozpmT/E0iPtmFIB46ZmdtAc9eNBvH0H/ZpiBw==';
  link.crossOrigin = 'anonymous';
  link.referrerPolicy = 'no-referrer';
  
  document.head.appendChild(link);
  
  console.log('✅ Font Awesome initialized');
}

/**
 * Replace all existing Lucide icons with Font Awesome icons
 * This can be used to migrate existing content
 */
export function replaceLucideWithFontAwesome() {
  const lucideIcons = document.querySelectorAll('.lucide-icon');
  
  lucideIcons.forEach(iconElement => {
    // Try to determine the icon name from the SVG content or data attributes
    const svg = iconElement.querySelector('svg');
    if (svg) {
      // This is a simplified approach - you might need more sophisticated detection
      const iconName = iconElement.dataset.iconName || 'info';
      const classes = iconElement.className.replace('lucide-icon', '').trim();
      
      // Create new Font Awesome icon
      const newIcon = document.createElement('span');
      newIcon.innerHTML = iconFont(iconName, classes);
      
      // Replace the old icon
      iconElement.parentNode.replaceChild(newIcon.firstChild, iconElement);
    }
  });
  
  console.log('✅ Replaced Lucide icons with Font Awesome');
}

// Export the icon mapping for reference
export { iconFontMap };